import * as React from 'react'
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid'
import { Box, Stack, Typography, styled } from '@mui/material'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'

// ─── Wrapper ──────────────────────────────────────────────────────────────────

const DataGridWrapper = styled(Box, {
  shouldForwardProp: (p) => p !== 'flat' && p !== 'fill',
})(({ theme, flat, fill }) => ({
  border: flat ? 'none' : `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  // When the grid is NOT autoHeight, it relies on a bounded parent for its
  // internal vertical + horizontal scroll — fill the height the caller gives us.
  ...(fill ? { height: '100%' } : {}),
}))

// ─── Styled DataGrid ──────────────────────────────────────────────────────────

const StyledDataGrid = styled(DataGrid, {
  shouldForwardProp: (p) => p !== 'headerBorderRadius',
})(({ theme, headerBorderRadius }) => ({
  border: 'none',
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.background.paper,
  width: '100%',

  // ── Column headers ──────────────────────────────────────────────────────────
  '& .MuiDataGrid-columnHeaders': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRadius: headerBorderRadius ?? 0,
    maxHeight: 'none !important',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
    whiteSpace: 'normal',
    lineHeight: 1.3,
    overflow: 'visible',
    textOverflow: 'unset',
  },
  '& .MuiDataGrid-columnHeader': {
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    '&:focus, &:focus-within': { outline: 'none' },
  },
  '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox)': {
    height: 'auto !important',
    borderRadius: theme.shape.borderRadius,
  },
  '& .MuiDataGrid-columnHeaderCheckbox': {
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 !important',
  },
  '& .MuiDataGrid-columnSeparator': { color: theme.palette.divider },
  '& .MuiDataGrid-sortIcon': { color: theme.palette.text.secondary },
  '& .MuiDataGrid-menuIconButton': {
    color: theme.palette.text.disabled,
    '&:hover': { color: theme.palette.text.secondary },
  },

  // ── Rows — base (no zebra here; applied via getRowClassName below) ──────────
  '& .MuiDataGrid-row': {
    borderBottom: `1px solid #e8ecf1`,
    transition: 'background-color 0.15s ease',
    maxHeight: 'none !important',
    '&:hover': { backgroundColor: theme.palette.action.hover },
    '&.Mui-selected': {
      backgroundColor: '#e3edfd',
      '&:hover': { backgroundColor: '#d6e4fc' },
    },
  },
  // Zebra — applied via getRowClassName (indexRelativeToCurrentPage).
  // nth-of-type is unreliable in DataGrid's virtualised DOM; class injection
  // always reflects the correct visible row position.
  '& .prozo-stripe': { backgroundColor: '#fafbfc' },
  '& .prozo-stripe:hover': { backgroundColor: `${theme.palette.action.hover} !important` },
  '& .prozo-stripe.Mui-selected': { backgroundColor: '#dce8fb' },

  // ── Cells ───────────────────────────────────────────────────────────────────
  '& .MuiDataGrid-cell': {
    borderBottom: 'none',
    fontSize: '0.8125rem',
    color: theme.palette.text.primary,
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    '&:focus, &:focus-within': { outline: 'none' },
  },

  // ── Footer / Pagination ─────────────────────────────────────────────────────
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${theme.palette.divider}`,
    minHeight: 52,
  },
  '& .MuiTablePagination-root': {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: '0.8125rem',
    margin: 0,
  },

  // ── Checkbox ────────────────────────────────────────────────────────────────
  '& .MuiCheckbox-root': {
    padding: theme.spacing(0.5),
    color: theme.palette.text.disabled,
    borderRadius: theme.shape.borderRadius,
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(18),
    },
    '&.Mui-checked': { color: theme.palette.primary.main },
  },

  // ── Scrollbar ───────────────────────────────────────────────────────────────
  '& ::-webkit-scrollbar': { height: 6, width: 6 },
  '& ::-webkit-scrollbar-track': { background: '#f5f5f5', borderRadius: 3 },
  '& ::-webkit-scrollbar-thumb': {
    background: '#c5c5c5',
    borderRadius: 3,
    '&:hover': { background: '#9e9e9e' },
  },
}))

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyOverlay() {
  return (
    <Stack height='100%' alignItems='center' justifyContent='center' gap={1} py={4}>
      <InboxOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', opacity: 0.5 }} />
      <Typography variant='body2' color='text.secondary'>
        No results found
      </Typography>
    </Stack>
  )
}

// ─── Toolbar ──────────────────────────────────────────────────────────────────

function CustomToolbar({ exportFileName }) {
  return (
    <GridToolbarContainer>
      <GridToolbarExport
        printOptions={{ disableToolbarButton: true }}
        csvOptions={{ fileName: exportFileName ?? `export_${new Date().toISOString().slice(0, 10)}` }}
      />
    </GridToolbarContainer>
  )
}

function getComponents(exportToExcel, exportFileName) {
  return {
    NoRowsOverlay: EmptyOverlay,
    NoResultsOverlay: EmptyOverlay,
    ...(exportToExcel && {
      Toolbar: () => <CustomToolbar exportFileName={exportFileName} />,
    }),
  }
}

// ─── ProzoDataGrid ────────────────────────────────────────────────────────────

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 15, 20, 50, 100]

export default function ProzoDataGrid(props) {
  const {
    rows,
    columns,
    getRowHeight,
    columnVisibilityModel,
    disableColumnMenu,
    pagination,
    paginationMode,
    isRowSelectable,
    disableColumnSelector,
    checkboxSelection = false,
    disableColumnFilter,
    rowSpacing = false,
    rowCount,
    page,
    setPage,
    sizePerPage,
    setSizePerPage,
    getRowClassName,
    hideCountNumber,
    rowSelectionModel,
    setRowSelectionModel,
    keepNonExistentRowsSelected,
    hideFooterPagination,
    rowHeight,
    exportFileName = 'proship',
    headerBorderRadius,
    flat = true,
    // Default true preserves the legacy behaviour for every existing caller.
    // Pass false (with a bounded-height parent) to get a grid that scrolls
    // internally on both axes — needed for very wide tables.
    autoHeight = true,
    // Optional; must include sizePerPage, or MUI hides the rows-per-page selector.
    rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
    ...restProps
  } = props

  const getRowSpacing = React.useCallback((params) => ({
    top: params.isFirstVisible ? 0 : 1,
    bottom: params.isLastVisible ? 0 : 1,
  }), [])

  return (
    <DataGridWrapper flat={flat} fill={!autoHeight}>
      <StyledDataGrid
        headerBorderRadius={headerBorderRadius}
        //density='compact'
        sx={!autoHeight ? { height: '100%' } : undefined}
        columns={columns}
        rows={rows}
        disableSelectionOnClick={true}
        rowCount={rowCount}
        checkboxSelection={checkboxSelection}
        selectionModel={rowSelectionModel}
        onSelectionModelChange={(m) => setRowSelectionModel?.(m)}
        keepNonExistentRowsSelected={keepNonExistentRowsSelected}
        paginationMode={paginationMode ?? 'server'}
        rowsPerPageOptions={rowsPerPageOptions}
        pagination={pagination ?? true}
        columnVisibilityModel={columnVisibilityModel ?? {}}
        hideFooterPagination={hideFooterPagination ?? false}
        disableColumnFilter={disableColumnFilter ?? true}
        disableColumnMenu={disableColumnMenu ?? true}
        disableColumnSelector={disableColumnSelector ?? true}
        onPageSizeChange={setSizePerPage}
        onPageChange={setPage}
        autoHeight={autoHeight}
        getRowHeight={getRowHeight || (() => 'auto')}
        rowHeight={rowHeight || 48}
        page={page}
        pageSize={sizePerPage}
        getRowId={props.getRowId}
        isRowSelectable={isRowSelectable}
        getRowSpacing={rowSpacing ? getRowSpacing : undefined}
        getRowClassName={(params) => {
          const getRowId = props.getRowId || ((r) => r.id)
          const idx = rows.findIndex((r) => getRowId(r) === params.id)
          const stripe = idx % 2 !== 0 ? 'prozo-stripe' : ''
          const user = getRowClassName ? getRowClassName(params) : ''
          return [stripe, user].filter(Boolean).join(' ')
        }}
        components={getComponents(props.exportToExcel || false, exportFileName)}
        localeText={{
          MuiTablePagination: {
            labelDisplayedRows: ({ from, to, count }) =>
              hideCountNumber || count === Number.MAX_VALUE
                ? `Showing ${from}–${to} of many`
                : `Showing ${from}–${to} of ${count} results`,
          },
        }}
        componentsProps={{
          toolbar: {
            printOptions: { disableToolbarButton: true },
            csvOptions: { disableToolbarButton: false },
          },
        }}
        {...restProps}
      />
    </DataGridWrapper>
  )
}
